import { useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { translate } from '../../i18n';
import { toApiYmd } from '../../utils/dateUtil';
import { executeDeleteLotteryReservation } from '../../dataAccess/webApi/dao/lotteryReservationsDao';
import { GetUseReasonDto } from '../../dataAccess/webApi/dto/useReasonDto';
import { LotteryReservationCancel } from '../../views/components/common/lotteryReservationDetail/lotteryReservationDetailGItems';
import { selectLotteryReserveCancelEntry, clearLotteryReserveCancel } from '../common/store/slices/lotteryReserveCancel';
import { reservationStatusListUrl } from '../common/constant/appUrl'
import { lotteryMethod, yesNo } from '../common/constant/classification'
import { OperationId } from '../common/constant/operationLog'
import { useErrorHandle } from '../common/error/errorHandler'
import { useOperationLog } from '../common/operationLog'
import { notifyMessage, showLoading } from '../common/store/slices/application'
import { tabValue } from '../reservationStatusList/reservationStatusListService';
import { selectSystemControl } from '../common/store/slices/systemControl';


interface ReservationDetail extends LotteryReservationCancel {
  useReason: GetUseReasonDto
  updateDatetime: string
}

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const history = useHistory()
  const { addOperationLog } = useOperationLog()
  const lotteryReservation = useSelector(selectLotteryReserveCancelEntry);
  const sysCtrl = useSelector(selectSystemControl)
  const showSiblingsLotteryResultReferenceFlag = sysCtrl.lotteryMethod === lotteryMethod.byParent

  const [deleteResultCode, setDeleteResultCode] = useState<number>();

  const reservationDetail = useMemo<ReservationDetail | null>(() => {
    if (!lotteryReservation) return null;
    const { usageDatetimes, facility: facilityData, goingTimes, ... other } = lotteryReservation;

    return {
      ... other,
      ... facilityData,
      status: usageDatetimes[0].status,
      goingTime: goingTimes[0],
      usageDate: usageDatetimes[0].usageDate,
      useFromDatetime: usageDatetimes[0].useFromDatetime,
      useToDatetime: usageDatetimes[0].useToDatetime,
    };
  }, [lotteryReservation])

  // 初期化
  useEffect(() => {
    if (!lotteryReservation) return;

    addOperationLog({
      operationId: OperationId.OP_00000001,
      accessData: [{
          userIdRegFlag: yesNo.yes,
          childId: lotteryReservation.childInfoList[0].childId,
          usageDate: toApiYmd(lotteryReservation.usageDatetimes[0].usageDate),
          reservationNo: lotteryReservation.reservationNo
      }],
    });
  }, [])

  // キャンセル処理
  const decide = useCallback(() => {
    if (!lotteryReservation) return;

    addOperationLog({
      operationId: OperationId.OP_00000037,
      accessData: [{
        userIdRegFlag: yesNo.yes,
        childId: lotteryReservation.childInfoList[0].childId,
        usageDate: toApiYmd(lotteryReservation.usageDatetimes[0].usageDate),
        reservationNo: lotteryReservation.reservationNo
      }]
    });

    dispatch(
      showLoading({
        process: errorHandle(async () => {
          const response = await executeDeleteLotteryReservation(lotteryReservation.reservationNo, {
            cancelReasonCategory: lotteryReservation.cancelReason.select,
            updateDatetime: lotteryReservation.updateDatetime
          });
          if (response.resultCode) {
            // キャンセル失敗時
            setDeleteResultCode(response.resultCode);
          } else {
            // キャンセル成功時
            dispatch(notifyMessage(translate('facilityLotteryReservationCancelConfirmation.success.completionMessage')));
            history.push(reservationStatusListUrl.url(), {
              tabValue: tabValue.lotteryReservation
            })
            dispatch(clearLotteryReserveCancel());
          }
        }),
        isHiddenMain: false,
      })
    );
  }, [lotteryReservation, addOperationLog, dispatch, errorHandle, history])

  return {
    lotteryReservation,
    deleteResultCode,
    reservationDetail,
    showSiblingsLotteryResultReferenceFlag,
    decide
  }
}
